import { useState, useEffect } from 'react';
import { CoverageRouter } from '../../components/coverage';
import { CoverageService } from '../../services/climate/coverage/CoverageService';
import { Card, CardHeader, CardContent, Button } from '@etherisc/ui-kit';
import { AlertTriangle, RefreshCw, Info, ExternalLink, Wifi, WifiOff } from 'lucide-react';
import type { 
  CoverageItem, 
  CoverageSummary, 
  RegionalCoverageSummary,
  CoverageDetailView,
  CoverageFilters
} from '../../types/coverage';

// Enhanced error types for better user feedback
interface DetailedError {
  type: 'network' | 'api' | 'auth' | 'data' | 'timeout' | 'unknown';
  message: string;
  details: string;
  suggestions: string[];
  techDetails?: string;
  canRetry: boolean;
}

// Parse errors into user-friendly format
function parseError(error: unknown, context: string): DetailedError {
  const baseUrl = '/api/climate'; // Now using our backend proxy
  
  if (error instanceof Error) {
    // Network/Connection errors
    if (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('ERR_NETWORK')) {
      return {
        type: 'network',
        message: 'Unable to connect to Backend API',
        details: `Cannot reach the backend API at ${baseUrl}`,
        suggestions: [
          'Check if the backend server is running',
          'Verify your authentication token is valid',
          'Check your network connection',
          'Contact your system administrator if the problem persists'
        ],
        techDetails: error.message,
        canRetry: true
      };
    }
    
    // Timeout errors
    if (error.message.includes('timeout') || error.message.includes('aborted')) {
      return {
        type: 'timeout',
        message: 'Request timed out',
        details: 'The Climate Server took too long to respond',
        suggestions: [
          'The server might be under heavy load',
          'Try again in a few moments',
          'Check server performance with your administrator'
        ],
        techDetails: error.message,
        canRetry: true
      };
    }
    
    // HTTP errors
    if (error.message.includes('404')) {
      return {
        type: 'api',
        message: 'Service endpoint not found',
        details: `The coverage API endpoint doesn't exist at ${baseUrl}`,
        suggestions: [
          'The backend may not have climate endpoints implemented yet',
          'Check if you\'re using the correct API version',
          'Contact the development team about climate API availability'
        ],
        techDetails: error.message,
        canRetry: false
      };
    }
    
    if (error.message.includes('401') || error.message.includes('403')) {
      return {
        type: 'auth',
        message: 'Authentication required',
        details: 'You don\'t have permission to access climate data',
        suggestions: [
          'Check if you\'re logged in properly',
          'Verify your user permissions include climate:read access',
          'Contact your administrator for the correct permissions'
        ],
        techDetails: error.message,
        canRetry: false
      };
    }
    
    if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
      return {
        type: 'api',
        message: 'Backend Server error',
        details: 'The backend server encountered an internal error',
        suggestions: [
          'This could be a backend or external Climate API issue',
          'Try again in a few minutes',
          'Report this to your system administrator if it continues'
        ],
        techDetails: error.message,
        canRetry: true
      };
    }
  }
  
  // Default unknown error
  return {
    type: 'unknown',
    message: `Failed to ${context}`,
    details: 'An unexpected error occurred while processing your request',
    suggestions: [
      'Try refreshing the page',
      'Check your internet connection',
      'Contact technical support if the issue persists'
    ],
    techDetails: error instanceof Error ? error.message : String(error),
    canRetry: true
  };
}

// Initialize the coverage service with backend proxy config
const coverageService = new CoverageService({
  baseUrl: '', // Empty for same-origin requests to our backend
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  enableLogging: true
});

export function CoverageManagement() {
  const [summaryData, setSummaryData] = useState<CoverageSummary | null>(null);
  const [regionalSummaries, setRegionalSummaries] = useState<RegionalCoverageSummary[]>([]);
  const [listData, setListData] = useState<CoverageItem[]>([]);
  const [detailData, setDetailData] = useState<CoverageDetailView | null>(null);
  
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  
  const [error, setError] = useState<DetailedError | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'disconnected'>('unknown');

  // Test API connectivity through our backend
  const testConnection = async () => {
    try {
      const healthResponse = await coverageService.getClimateHealth();
      const climateApi = healthResponse.climate_api as { status?: string };
      const isConnected = healthResponse.success && climateApi?.status === 'connected';
      setConnectionStatus(isConnected ? 'connected' : 'disconnected');
      return isConnected;
    } catch {
      setConnectionStatus('disconnected');
      return false;
    }
  };

  // Load initial data
  useEffect(() => {
    Promise.all([
      testConnection(),
      loadSummaryData(),
      loadListData(),
      loadRegionalSummaries()
    ]);
  }, []);

  const loadSummaryData = async () => {
    try {
      setSummaryLoading(true);
      setError(null);
      const response = await coverageService.getCoverageSummary();
      setSummaryData(response.data);
      setConnectionStatus('connected');
    } catch (err) {
      console.error('Failed to load coverage summary:', err);
      const detailedError = parseError(err, 'load coverage summary');
      setError(detailedError);
      setConnectionStatus('disconnected');
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadListData = async (filters?: Partial<CoverageFilters>) => {
    try {
      setListLoading(true);
      setError(null);
      
      // Convert our filters to the API format
      const apiParams = {
        limit: 50,
        ...(filters?.search && { search: filters.search }),
        ...(filters?.dataSource?.[0] && { data_source: filters.dataSource[0] }),
        ...(filters?.regionId?.[0] && { region_id: filters.regionId[0] }),
        ...(filters?.coverageType?.[0] && { coverage_type: filters.coverageType[0] }),
        ...(filters?.status?.[0] && { status: filters.status[0] as 'active' | 'inactive' | 'pending' | 'payment_collection_expired' })
      };
      
      const response = await coverageService.getCoverage(apiParams);
      setListData(response.data.coverage);
      setConnectionStatus('connected');
    } catch (err) {
      console.error('Failed to load coverage list:', err);
      const detailedError = parseError(err, 'load coverage data');
      setError(detailedError);
      setConnectionStatus('disconnected');
    } finally {
      setListLoading(false);
    }
  };

  const loadRegionalSummaries = async () => {
    try {
      // Get unique regions from the coverage data and load summaries
      const response = await coverageService.getCoverage({ limit: 100 });
      const uniqueRegions = new Set(response.data.coverage.map(item => item.region_id));
      
      const summaries: RegionalCoverageSummary[] = [];
      for (const regionId of uniqueRegions) {
        try {
          // Create a mock regional summary since the API doesn't have this endpoint yet
          const regionItems = response.data.coverage.filter(item => item.region_id === regionId);
          const summary: RegionalCoverageSummary = {
            regionId,
            regionName: regionItems[0]?.region_name || `Region ${regionId}`,
            totalCoverage: regionItems.length,
            activeCoverage: regionItems.filter(item => item.status === 'active').length,
            dataQuality: 'good',
            lastUpdated: new Date().toISOString(),
            coverageByType: []
          };
          summaries.push(summary);
        } catch (err) {
          console.warn(`Failed to load summary for region ${regionId}:`, err);
        }
      }
      
      setRegionalSummaries(summaries);
    } catch (err) {
      console.error('Failed to load regional summaries:', err);
      // Don't set error here as this is secondary data
    }
  };

  const handleViewDetails = async (item: CoverageItem) => {
    try {
      setDetailLoading(true);
      setError(null);
      
      // Use getCoverageById for details
      const response = await coverageService.getCoverageById(item.id);
      
      // Convert to detail view format
      const detailData: CoverageDetailView = {
        basic: {
          ...response.data,
          spatial_extent: response.data.spatial_extent || {
            bounds: [-74.2, 40.5, -73.7, 41.0],
            geometry: undefined
          },
          temporal_extent: response.data.temporal_extent || {
            start: item.start_date,
            end: item.end_date,
            resolution: 'daily'
          },
          data_quality: response.data.data_quality || {
            completeness: 95,
            accuracy: 98,
            last_validated: new Date().toISOString()
          },
          statistics: response.data.statistics || {
            min_value: 0,
            max_value: 100,
            mean_value: 50,
            std_deviation: 10
          }
        }
      } as CoverageDetailView;
      
      setDetailData(detailData);
    } catch (err) {
      console.error('Failed to load coverage details:', err);
      const detailedError = parseError(err, 'load coverage details');
      setError(detailedError);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRefreshSummary = () => {
    loadSummaryData();
  };

  const handleRefreshList = () => {
    loadListData();
  };

  const handleFiltersChange = (filters: Partial<CoverageFilters>) => {
    loadListData(filters);
  };

  const handleExport = async (selectedItems: CoverageItem[]) => {
    try {
      // For now, just log the export request since the API doesn't have this endpoint yet
      console.log('Export initiated for items:', selectedItems.map(item => item.id));
      // In a real implementation, this would trigger a download
    } catch (err) {
      console.error('Failed to export coverage data:', err);
      const detailedError = parseError(err, 'export coverage data');
      setError(detailedError);
    }
  };

  const handleRetry = () => {
    setError(null);
    loadSummaryData();
    loadListData();
    loadRegionalSummaries();
  };

  // Show detailed error state with full transparency
  if (error && !summaryData && !listData.length) {
    return (
      <div className="p-6 space-y-6">
        {/* Connection Status */}
        <div className="flex items-center gap-2 text-sm">
          {connectionStatus === 'connected' ? (
            <><Wifi className="w-4 h-4 text-green-600" /> <span className="text-green-600">Connected</span></>
          ) : connectionStatus === 'disconnected' ? (
            <><WifiOff className="w-4 h-4 text-red-600" /> <span className="text-red-600">Disconnected</span></>
          ) : (
            <><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /> <span className="text-gray-600">Checking connection...</span></>
          )}
        </div>

        {/* Main Error Card */}
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-lg font-semibold text-red-800">{error.message}</h3>
                <p className="text-red-700 mt-1">{error.details}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* What you can do */}
              <div>
                <h4 className="font-medium text-red-800 mb-2">What you can do:</h4>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  {error.suggestions.map((suggestion, index) => (
                    <li key={index}>{suggestion}</li>
                  ))}
                </ul>
              </div>

              {/* Technical details (for developers) */}
              {error.techDetails && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-red-800 font-medium mb-2">
                    Technical Details
                  </summary>
                  <div className="bg-red-100 p-3 rounded text-sm font-mono text-red-900 border">
                    {error.techDetails}
                  </div>
                </details>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-4">
                {error.canRetry && (
                  <Button 
                    onClick={handleRetry}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </Button>
                )}
                
                <Button 
                  variant="outline"
                  onClick={() => window.open('/api/climate/health', '_blank')}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Check API Status
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Information */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-blue-800">System Information</h3>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                             <div>
                 <dt className="font-medium text-blue-800">Climate API URL:</dt>
                 <dd className="text-blue-700 font-mono">/api/climate (Backend Proxy)</dd>
               </div>
              <div>
                <dt className="font-medium text-blue-800">Connection Status:</dt>
                <dd className="text-blue-700 capitalize">{connectionStatus}</dd>
              </div>
              <div>
                <dt className="font-medium text-blue-800">Error Type:</dt>
                <dd className="text-blue-700 capitalize">{error.type}</dd>
              </div>
              <div>
                <dt className="font-medium text-blue-800">Environment:</dt>
                <dd className="text-blue-700">Backend Proxy</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Connection Status Indicator */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          {connectionStatus === 'connected' ? (
            <><Wifi className="w-4 h-4 text-green-600" /> <span className="text-green-600">Climate API Connected</span></>
          ) : connectionStatus === 'disconnected' ? (
            <><WifiOff className="w-4 h-4 text-yellow-600" /> <span className="text-yellow-600">Limited functionality - API unavailable</span></>
          ) : (
            <><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /> <span className="text-gray-600">Checking connection...</span></>
          )}
        </div>
      </div>

      <CoverageRouter
        summaryData={summaryData}
        regionalSummaries={regionalSummaries}
        listData={listData}
        detailData={detailData}
        summaryLoading={summaryLoading}
        listLoading={listLoading}
        detailLoading={detailLoading}
        onViewDetails={handleViewDetails}
        onRefreshSummary={handleRefreshSummary}
        onRefreshList={handleRefreshList}
        onFiltersChange={handleFiltersChange}
        onExport={handleExport}
        initialView="summary"
      />
    </div>
  );
} 